import { useState, useEffect } from 'react';
import { fetchSSE } from '@/utils/fetchSSE';

export interface MatchStatsAttempt {
  id: string;
  timestamp: string;
  fixtureId: string;
  leagueId?: string;
  status: 'cached' | 'success' | 'error';
  durationMs: number;
  teamStatsCount?: number;
  playerStatsCount?: number;
  error?: string;
}

interface SSEMessage {
  type: 'initial_data' | 'new_attempt' | 'alert' | 'heartbeat';
  attempts?: MatchStatsAttempt[];
  attempt?: MatchStatsAttempt;
  consecutiveFailures?: number;
  alertActive?: boolean;
}

export function useMatchStatsFeedSSE(fixtureId: string | null) {
  const [attempts, setAttempts] = useState<MatchStatsAttempt[]>([]);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [alertActive, setAlertActive] = useState(false);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!fixtureId) return;

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const url = `${apiUrl}/api/admin/live-scoring/fixture/${fixtureId}/match-stats/stream`;

    const abort = new AbortController();
    setConnected(true);

    fetchSSE(
      url,
      data => {
        const msg = data as SSEMessage;
        if (msg.type === 'initial_data') {
          setAttempts(msg.attempts ?? []);
          setConsecutiveFailures(msg.consecutiveFailures ?? 0);
          setAlertActive(msg.alertActive ?? false);
        } else if (msg.type === 'new_attempt' && msg.attempt) {
          setAttempts(prev => [msg.attempt!, ...prev]);
          setConsecutiveFailures(msg.consecutiveFailures ?? 0);
          setAlertActive(msg.alertActive ?? false);
        } else if (msg.type === 'alert') {
          setAlertActive(true);
          setConsecutiveFailures(msg.consecutiveFailures ?? 0);
        }
      },
      abort.signal
    )
      .catch(() => {
        // ignore — covered by connected state
      })
      .finally(() => {
        if (!abort.signal.aborted) setConnected(false);
      });

    return () => {
      abort.abort();
      setConnected(false);
    };
  }, [fixtureId]);

  return { attempts, consecutiveFailures, alertActive, connected };
}
